import { Vector, Line, Box } from './geometry'
import each from 'lodash/each'
import reduce from 'lodash/reduce'
import throttle from 'lodash/throttle'
import size from 'lodash/size'
import isEqual from 'lodash/isEqual'
import direction from 'constants/direction'
import { isEmbeddedReadOnly } from 'lib/embed'
import { physicsSettings, layoutSettings, updateSpringLength } from './settings'
import { Simulation, Point } from './simulation'
import type { Shape, ListSettings } from 'types/shapes'

const { DIRECTED, REVERSE, BIDIRECTIONAL } = direction
const edgeOffset = 0 // set to 3 for visually showing the two lines of a loop edge


class ForceDirectedLayout {
  [key: string]: any

  constructor(attrs: any) {
    const { increaseDepth, decreaseDepth, resetDepth } = attrs
    this.increaseDepth = increaseDepth
    this.decreaseDepth = decreaseDepth
    this.resetDepth = resetDepth
    this.graph = {
      nodes: {},
      edges: {},
      controls: {},
      modified: 0
    }
    this.dimensions = {
      center: new Vector(0, 0),
      window: new Vector(0, 0),
      box: new Box(new Vector(0, 0), 0, 0),
      mouse: new Vector(0, 0),
      mouseOffset: new Vector(0, 0),
      drag: {
        isDragging: false,
        id: null
      }
    }
    this.creating = {
      isCreating: false,
      ids: []
    }
    this.shapes = {} as Record<string, { shapeType: string; ids: string[]; targets: Record<string, { x: number; y: number }>; settings: ListSettings }>
    this.settings = layoutSettings
    this._pendingZoom = null
    this.simulation = new Simulation(physicsSettings, this.graph, this.dimensions, this.creating, this.shapes)
    this.simulation.addEventListener('render', () => {
      this.render()
      this.adjustDepth()
    })
    this.simulation.addEventListener('preframe', () => {
      this.applyPendingZoom()
    })
    this.simulation.addEventListener('stop', () => this.adjustDepth())
    this.setGraphDimensions(0, 0, 0, 0)
  }

  setCanvasCtx(ctx: any, canvasScale: any) {
    this.ctx = ctx
    this.canvasScale = canvasScale
  }

  notifySimulation() {
    this.simulation.start()
  }

  setGraphDimensions(width: any, height: any, windowWidth: any, windowHeight: any) {
    this.width = width
    this.height = height
    this.dimensions.window.set_m(windowWidth, windowHeight)
    this.dimensions.center.set_m(this.width / 2, this.height / 2)
    this.dimensions.box = new Box(new Vector(0, 0), this.width, this.height)
    updateSpringLength(this.width)
    this.notifySimulation()
  }

  scalePositions(shiftX: number, shiftY: number) {
    each(this.graph.nodes, (n: any) => {
      n.position.x += shiftX
      n.position.y += shiftY
    })
    each(this.graph.controls, (c: any) => {
      if (c.position) {
        c.position.x += shiftX
        c.position.y += shiftY
      }
    })
  }

  queueZoom(params: { newW: number; newH: number; shiftX: number; shiftY: number; windowWidth: any; windowHeight: any; onApplied?: () => void }) {
    this._pendingZoom = params
    this.notifySimulation()
  }

  applyPendingZoom() {
    const z = this._pendingZoom
    if (!z) return
    this._pendingZoom = null
    this.scalePositions(z.shiftX, z.shiftY)
    // Inline dimension update — avoid setGraphDimensions which calls notifySimulation
    this.width = z.newW
    this.height = z.newH
    this.dimensions.window.set_m(z.windowWidth, z.windowHeight)
    this.dimensions.center.set_m(this.width / 2, this.height / 2)
    this.dimensions.box = new Box(new Vector(0, 0), this.width, this.height)
    updateSpringLength(this.width)
    if (z.onApplied) z.onApplied()
  }

  setMousePosition(x: any, y: any) {
    this.dimensions.mouse.x = x
    this.dimensions.mouse.y = y
  }

  setDragOffset(x: any, y: any) {
    this.dimensions.mouseOffset.x = this.dimensions.mouse.x - x
    this.dimensions.mouseOffset.y = this.dimensions.mouse.y - y
  }

  setCurrentDepth(depth: any) {
    this.depth = depth
  }

  updateDragging(drag: any) {
    this.dimensions.drag = drag
    this.notifySimulation()
  }

  add(objectType: any, obj: any) {
    switch (objectType) {
      case 'node':
        this.addNode(obj)
        break
      case 'edge':
        this.addEdge(obj)
        break
      case 'control':
        this.addControl(obj)
        break
    }
  }

  addNode(node: any) {
    node.position = this.getInitialPosition(node)
    const n = new Node(node)
    this.ensureInside(n)
    this.graph.nodes[node.id] = n
    this.graph.modified = Date.now()
    this.notifySimulation()
  }

  addEdge(edge: any) {
    edge.position = this.getEdgeInitialPosition(edge)
    const e = new Edge(edge)
    this.ensureInside(e)
    this.graph.edges[edge.id] = e
    this.graph.nodes[edge.id] = e
    this.graph.modified = Date.now()
    this.notifySimulation()
  }

  addControl(control: any) {
    const c = new Control(control)
    this.ensureInside(c)
    this.graph.controls[control.id] = c
    this.notifySimulation()
  }

  remove(objectType: any, id: any) {
    switch (objectType) {
      case 'node':
        this.removeNode(id)
        break
      case 'edge':
        this.removeEdge(id)
        break
      case 'control':
        this.removeControl(id)
        break
    }
  }

  removeNode(id: any) {
    delete this.graph.nodes[id]
    this.notifySimulation()
  }

  removeControl(id: any) {
    delete this.graph.controls[id]
    this.notifySimulation()
  }

  removeEdge(id: any) {
    delete this.graph.edges[id]
    delete this.graph.nodes[id]
    this.notifySimulation()
  }

  setCreating(creating: any) {
    if (!isEqual(this.creating.ids, creating.ids)) this.creating.ids = creating.ids
    if (!isEqual(this.creating.isCreating, creating.isCreating)) this.creating.isCreating = creating.isCreating
    this.notifySimulation()
  }

  setShape(id: string, shape: Shape) {
    this.shapes[id] = {
      shapeType: shape.shapeType,
      ids: shape.entityIds,
      targets: {},
      settings: shape.settings,
    }
    this._computeShapeTargets(id)
    this.notifySimulation()
  }

  removeShape(id: string) {
    delete this.shapes[id]
    this.notifySimulation()
  }

  _computeAllShapeTargets() {
    for (const shapeId of Object.keys(this.shapes)) {
      this._computeShapeTargets(shapeId)
    }
  }

  _computeShapeTargets(shapeId: string) {
    const shape = this.shapes[shapeId]
    if (!shape) return
    switch (shape.shapeType) {
      case 'list':
        this._computeListTargets(shapeId)
        break
      default:
        break
    }
  }

  _computeListTargets(shapeId: string) {
    const shape = this.shapes[shapeId]
    if (!shape) return

    const ids = shape.ids
    const targets: Record<string, { x: number; y: number }> = {}
    if (ids.length === 0) {
      shape.targets = targets
      return
    }

    const wordGap = -4 + (shape.settings.spacing ?? 0)
    const lineGap = -10 + (shape.settings.spacing ?? 0)
    const wrapFactor = 0.75
    const viewW = this.dimensions.box.width
    const viewH = this.dimensions.box.height
    const isVertical = shape.settings.direction === 'vertical'
    const viewMain = isVertical ? viewH : viewW

    // Gather widths and heights (from layout nodes)
    const items: Array<{ id: string; w: number; h: number }> = []
    for (const id of ids) {
      const n = this.graph.nodes[id]
      if (!n) continue
      items.push({ id, w: n.width || 60, h: n.height || 30 })
    }
    if (items.length === 0) {
      shape.targets = targets
      return
    }

    const mainSize = (item: { w: number; h: number }) => (isVertical ? item.h : item.w)
    const crossSize = (item: { w: number; h: number }) => (isVertical ? item.w : item.h)

    // Arrange items into lines (rows when horizontal, columns when vertical) that wrap within the viewport
    const lines: Array<Array<{ id: string; w: number; h: number }>> = []
    let currentLine: Array<{ id: string; w: number; h: number }> = []
    let currentLineMain = 0

    for (const item of items) {
      const addedMain = currentLine.length > 0 ? mainSize(item) + wordGap : mainSize(item)
      if (currentLine.length > 0 && currentLineMain + addedMain > viewMain * wrapFactor) {
        lines.push(currentLine)
        currentLine = [item]
        currentLineMain = mainSize(item)
      } else {
        currentLine.push(item)
        currentLineMain += addedMain
      }
    }
    if (currentLine.length > 0) {
      lines.push(currentLine)
    }

    // Compute each line's cross size, main size, and the overall block dimensions
    const lineCrossSizes = lines.map((line) =>
      line.reduce((max, item) => Math.max(max, crossSize(item)), 0)
    )
    const lineMainSizes = lines.map((line) =>
      line.reduce((sum, item) => sum + mainSize(item), 0) + wordGap * (line.length - 1)
    )
    const maxLineMain = Math.max(...lineMainSizes)
    const totalCross = lineCrossSizes.reduce((sum, c) => sum + c, 0) + lineGap * (lines.length - 1)

    // Compute relative positions within the block (origin 0,0)
    const relTargets: Record<string, { x: number; y: number }> = {}
    let lineCrossOffset = 0
    for (let l = 0; l < lines.length; l++) {
      const line = lines[l]!
      const lineCross = lineCrossSizes[l]!
      let itemMainOffset = 0
      for (const item of line) {
        const cross = lineCrossOffset + (lineCross - crossSize(item)) / 2
        relTargets[item.id] = isVertical ? { x: cross, y: itemMainOffset } : { x: itemMainOffset, y: cross }
        itemMainOffset += mainSize(item) + wordGap
      }
      lineCrossOffset += lineCross + lineGap
    }

    // Extent of the whole block along each axis, for clamping to graph bounds
    const blockWidthExtent = isVertical ? totalCross : maxLineMain
    const blockHeightExtent = isVertical ? maxLineMain : totalCross

    // Determine block origin: anchor on dragged member or centroid
    let blockLeft: number
    let blockTop: number
    const dragId = this.dimensions.drag.isDragging ? this.dimensions.drag.id : null
    const draggedRel = dragId ? relTargets[dragId] : null
    const draggedNode = dragId ? this.graph.nodes[dragId] : null

    if (draggedRel && draggedNode) {
      // Anchor so dragged member's slot aligns with its current position
      blockLeft = draggedNode.position.x - draggedRel.x
      blockTop = draggedNode.position.y - draggedRel.y
    } else {
      // Average implied block origin from each node's position and its relative slot
      let sumLeft = 0
      let sumTop = 0
      let count = 0
      for (const item of items) {
        const n = this.graph.nodes[item.id]
        const rel = relTargets[item.id]
        if (n && rel) {
          sumLeft += n.position.x - rel.x
          sumTop += n.position.y - rel.y
          count++
        }
      }
      blockLeft = count > 0 ? sumLeft / count : 0
      blockTop = count > 0 ? sumTop / count : 0
    }

    // Clamp block to graph bounds
    blockLeft = Math.max(0, Math.min(blockLeft, viewW - blockWidthExtent))
    blockTop = Math.max(0, Math.min(blockTop, viewH - blockHeightExtent))

    // Assign absolute targets
    for (const item of items) {
      const rel = relTargets[item.id]
      if (rel) {
        targets[item.id] = { x: blockLeft + rel.x, y: blockTop + rel.y }
      }
    }

    shape.targets = targets
  }

  setSize(objectType: any, id: any, width: any, height: any) {
    switch (objectType) {
      case 'node':
        this.setNodeSize(id, width, height)
        break
      case 'edge':
        this.setEdgeSize(id, width, height)
        break
      case 'control':
        this.setControlSize(id, width, height)
        break
    }
  }

  setNodeSize(id: any, width: any, height: any) {
    this.graph.nodes[id].setSize(width, height)
    this.notifySimulation()
  }

  setEdgeSize(id: any, width: any, height: any) {
    this.graph.edges[id].setSize(width, height)
    this.notifySimulation()
  }

  setControlSize(id: any, width: any, height: any) {
    this.graph.controls[id].setSize(width, height)
    this.notifySimulation()
  }

  update(objectType: any, obj: any) {
    switch (objectType) {
      case 'node':
        this.updateNode(obj)
        break
      case 'edge':
        this.updateEdge(obj)
        break
    }
  }

  updateNode(n: any) {
    this.graph.nodes[n.id]?.update(n)
    this.notifySimulation()
  }

  updateEdge(e: any) {
    this.graph.edges[e.id]?.update(e)
    this.graph.nodes[e.id]?.update(e)
    this.notifySimulation()
  }

  render() {
    // Recompute shape targets each frame (node sizes may have changed)
    if (Object.keys(this.shapes).length > 0) {
      this._computeAllShapeTargets()
    }
    each(this.graph.nodes, (n: any) => {
      if (this._debugPending?.has(n.id)) return
      n.setStyle({
        left: `${n.position.x}px`,
        top: `${n.position.y}px`
      })
      n.setZoom(n.zoom)
    })
    this.setControlsPosition()
    each(this.graph.controls, (c: any) => {
      if (!(c.position?.x != null && c.position?.y != null)) return
      c.setStyle({
        left: `${c.position.x}px`,
        top: `${c.position.y}px`
      })
    })
    this.clearCanvas()
    this.drawDebugPlacement()
    const shapedTargets = this._getShapedTargets()
    each(this.graph.edges, (e: any) => {
      // Skip if the edge itself is in a shape
      if (shapedTargets[e.id]) return
      // Check per-shape edge-skipping rules
      if (this._shouldSkipEdge(e, shapedTargets)) return
      if (!e.visible) {
        // Invisible edges only draw while focused (themselves or an endpoint), and dashed
        if (!(e.focused || this._edgeHasFocusedEndpoint(e))) return
        this.drawEdgeArrows(e, true)
        return
      }
      this.drawEdgeArrows(e)
    })
    this.drawCreatingArrows()
  }

  _getShapedTargets(): Record<string, { x: number; y: number }> {
    const targets: Record<string, { x: number; y: number }> = {}
    const shapesMap = this.shapes as Record<string, { shapeType: string; ids: string[]; targets: Record<string, { x: number; y: number }>; settings: ListSettings }>
    for (const shape of Object.values(shapesMap)) {
      Object.assign(targets, shape.targets)
    }
    return targets
  }

  _edgeHasFocusedEndpoint(e: any) {
    const f = this.graph.nodes[e.from]
    const t = this.graph.nodes[e.to]
    return Boolean(f?.focused || t?.focused)
  }

  _shouldSkipEdge(e: any, shapedTargets: Record<string, { x: number; y: number }>) {
    const bothEndpointsInShape = shapedTargets[e.from] && shapedTargets[e.to]
    if (!bothEndpointsInShape) return false
    // Find a shape containing both endpoints; use its showInterItemEdges setting
    const shapesMap = this.shapes as Record<string, { shapeType: string; ids: string[]; targets: Record<string, { x: number; y: number }>; settings: ListSettings }>
    for (const shape of Object.values(shapesMap)) {
      const idsSet = new Set(shape.ids)
      if (idsSet.has(e.from) && idsSet.has(e.to)) {
        if (!shape.settings.showInterItemEdges) {
          if (!e.hasVisibleLabel()) return true
        }
      }
    }
    return false
  }

  setControlsPosition() {
    each(this.graph.controls, (c: any) => {
      const n = this.graph.nodes[c.id]
      if (!n) return
      const centerLine = new Line(n.getPosition(), this.dimensions.mouse)
      const l = n.getLineToPoint(this.dimensions.mouse)
      if (!l) return
      const pos = l.start.add(centerLine.originizedVector().normalize().mul(10))
      c.centerTo(pos)
    })
  }

  clearCanvas() {
    this.ctx.clearRect(0, 0, this.width * this.canvasScale, this.height * this.canvasScale)
  }

  drawDebugPlacement() {
    if (!this._debugPlacement) return
    const { topCandidates, chosen, neighborCenters } = this._debugPlacement
    const s = this.canvasScale

    // Red dots for top 10% spacing candidates.
    this.ctx.globalAlpha = 0.6
    this.ctx.fillStyle = 'red'
    for (const c of topCandidates) {
      this.ctx.beginPath()
      this.ctx.arc(c.x * s, c.y * s, 4 * s, 0, Math.PI * 2)
      this.ctx.fill()
    }

    // Blue circles for neighbor positions.
    this.ctx.strokeStyle = 'cyan'
    this.ctx.lineWidth = 2 * s
    this.ctx.globalAlpha = 0.9
    for (const nb of neighborCenters) {
      this.ctx.beginPath()
      this.ctx.arc(nb.x * s, nb.y * s, 10 * s, 0, Math.PI * 2)
      this.ctx.stroke()
    }

    // Green circle for chosen point.
    this.ctx.strokeStyle = 'lime'
    this.ctx.lineWidth = 3 * s
    this.ctx.globalAlpha = 1
    this.ctx.beginPath()
    this.ctx.arc(chosen.x * s, chosen.y * s, 8 * s, 0, Math.PI * 2)
    this.ctx.stroke()
  }

  drawCreatingArrows() {
    if (this.creating.isCreating) {
      each(this.creating.ids, (id: any) => {
        const f = this.graph.nodes[id]
        if (!f) return
        const l = f.getLineToPoint(this.dimensions.mouse)
        if (!l) return
        this.drawArrow(l, true, f.getColor(), Math.pow(0.9, f.depth - 1), (f.getLineWidth && f.getLineWidth()) || 1)
      })
    }
  }

  drawEdgeArrows(edge: any, dashed = false) {
    if (edge.hasVisibleLabel()) {
      this.drawEdgeArrow(edge, this.graph.nodes[edge.to], [DIRECTED, BIDIRECTIONAL].includes(edge.directionality), edgeOffset, dashed)
      this.drawEdgeArrow(edge, this.graph.nodes[edge.from], [REVERSE, BIDIRECTIONAL].includes(edge.directionality), -edgeOffset, dashed)
    } else {
      const fromNode = this.graph.nodes[edge.from]
      const toNode = this.graph.nodes[edge.to]
      if (!(fromNode && toNode)) return
      const l = fromNode.getLineToNode(toNode, edgeOffset)
      if (!l) return
      this.drawArrow(
        l,
        [DIRECTED, BIDIRECTIONAL].includes(edge.directionality),
        (edge.getColor && edge.getColor()) || 'white',
        Math.pow(0.9, toNode.depth - 1),
        (edge.getLineWidth && edge.getLineWidth()) || 1,
        dashed
      )
    }
  }

  drawEdgeArrow(f: any, t: any, withHead: any, displace: any, dashed = false) {
    const l = f.getLineToNode(t, displace)
    if (!l) return
    this.drawArrow(l, withHead, f.getColor(), Math.pow(0.9, t.depth - 1), (f.getLineWidth && f.getLineWidth()) || 1, dashed)
  }

  drawArrow(line: any, withHead: any, color: any, opacity: any, lineWidth = 1, dashed = false) {
    this.drawLine(line, color, opacity, lineWidth, dashed)
    if (withHead) this.drawArrowHead(line, color, opacity, lineWidth)
  }

  drawArrowHead(line: any, color: any, opacity: any, lineWidth = 1) {
    const central = line.end.sub(line.start).normalize_m().mul_m(this.settings.arrowHeadSize)
    const f = central.rotate((5 * Math.PI) / 6).add_m(line.end.x, line.end.y)
    const s = central.rotate((7 * Math.PI) / 6).add_m(line.end.x, line.end.y)
    this.ctx.beginPath()
    this._drawLine(line.end, f, color, opacity, lineWidth)
    this._drawLine(line.end, s, color, opacity, lineWidth)
    this.ctx.stroke()
  }

  drawLine(line: any, color: any, opacity: any, lineWidth = 1, dashed = false) {
    this.ctx.beginPath()
    this.ctx.setLineDash(dashed ? [lineWidth * 4, lineWidth * 3] : [])
    this._drawLine(line.start, line.end, color, opacity, lineWidth)
    this.ctx.stroke()
    this.ctx.setLineDash([])
  }

  _drawLine(from: any, to: any, color: any, opacity: any, lineWidth = 1) {
    from = from.mul(this.canvasScale)
    to = to.mul(this.canvasScale)
    this.ctx.globalAlpha = opacity
    this.ctx.strokeStyle = color
    this.ctx.lineWidth = lineWidth
    this.ctx.moveTo(from.x, from.y)
    this.ctx.lineTo(to.x, to.y)
  }

  adjustDepth = throttle(() => {
    const d = this.maxDepth()
    const area = this.width * this.height
    const curTot = this.getTotalSize()
    const decTot = this.getTotalSize(d - 1)
    if (curTot < area && d >= this.depth) {
      this.increaseDepth()
      this.notifySimulation()
    }
    if (decTot > area) {
      this.decreaseDepth()
      this.notifySimulation()
    }
  }, 500)

  getTotalSize(depth?: any) {
    const totalSize = reduce(
      this.graph.nodes,
      (tot: any, n: any) => {
        if (n.preserved) return tot
        if (depth != null && n.depth > depth) {
          return tot
        }
        let w = n.width
        let h = n.height
        let mod = physicsSettings.springLength * 2
        if (n instanceof Edge) {
          mod *= this.settings.nodeSizeMultiplier
        } else {
          mod *= this.settings.edgeSizeMultiplier
        }
        w += mod
        h += mod
        const nodeSize = w * h
        return tot + nodeSize
      },
      0
    )
    return totalSize
  }

  maxDepth() {
    let d = 0
    each(this.graph.nodes, (n: any) => {
      if (n.isMore || n.preserved) return
      d = Math.max(d, n.depth)
    })
    return d
  }

  getNeighbors(n: any) {
    const neighbors: any = {}
    if (n.to || n.from) {
      if (this.graph.nodes[n.to]) neighbors[n.to] = this.graph.nodes[n.to]
      if (this.graph.nodes[n.from]) neighbors[n.from] = this.graph.nodes[n.from]
    } else {
      each(n.neighbors, (_: any, id: any) => {
        if (this.graph.nodes[id]) neighbors[id] = this.graph.nodes[id]
      })
    }
    return neighbors
  }

  getEdgeInitialPosition(edge: any) {
    if (edge.initialPosition?.x != null && edge.initialPosition?.y != null) {
      return new Vector(edge.initialPosition.x, edge.initialPosition.y)
    }
    const getCenter = (obj: any) => {
      if (obj?.getPosition) return obj.getPosition()
      return new Vector(obj.position.x, obj.position.y)
    }
    const fromNode = this.graph.nodes[edge.from]
    const toNode = this.graph.nodes[edge.to]
    if (fromNode && toNode) {
      if (edge.from === edge.to) {
        const center = getCenter(fromNode)
        const boxW = this.dimensions.box.width
        const boxH = this.dimensions.box.height
        const dLeft = center.x
        const dRight = boxW - center.x
        const dTop = center.y
        const dBottom = boxH - center.y
        const maxDist = Math.max(dLeft, dRight, dTop, dBottom)
        let dir = new Vector(0, -1)
        if (maxDist === dLeft) dir = new Vector(-1, 0)
        else if (maxDist === dRight) dir = new Vector(1, 0)
        else if (maxDist === dBottom) dir = new Vector(0, 1)
        return center.add(dir.mul(20))
      }
      return getCenter(fromNode).add(getCenter(toNode)).div(2)
    }
    if (fromNode) return getCenter(fromNode)
    if (toNode) return getCenter(toNode)
    return new Vector(this.dimensions.box.width / 2, this.dimensions.box.height / 2)
  }

  getInitialPosition_old(node: any) {
    if (node.initialPosition?.x != null && node.initialPosition?.y != null) {
      return new Vector(node.initialPosition.x, node.initialPosition.y)
    }

    const clamp = (v: any, minV: any, maxV: any) => {
      if (v < minV) return minV
      if (v > maxV) return maxV
      return v
    }

    const hash32 = (s: any) => {
      s = `${s}`
      let h = 2166136261
      for (let i = 0; i < s.length; i += 1) {
        h ^= s.charCodeAt(i)
        h = (h * 16777619) >>> 0
      }
      return h >>> 0
    }

    const isOverlapping = (a: any, b: any) => {
      const aL = a.tl.x
      const aT = a.tl.y
      const aR = aL + a.width
      const aB = aT + a.height
      const bL = b.tl.x
      const bT = b.tl.y
      const bR = bL + b.width
      const bB = bT + b.height
      return !(aR <= bL || aL >= bR || aB <= bT || aT >= bB)
    }

    const getCenter = (obj: any) => {
      if (obj?.getPosition) {
        return obj.getPosition()
      }
      return new Vector(obj.position.x, obj.position.y)
    }

    // Use the viewport/box center as the default “spawn center”.
    let boxCenter = new Vector(this.dimensions.box.width / 2.0, this.dimensions.box.height / 2.0)
    if (isNaN(boxCenter.x) || isNaN(boxCenter.y) || (this.dimensions.box.width === 0 && this.dimensions.box.height === 0)) {
      boxCenter = this.dimensions.center.copy()
    }

    // Find a focused object in the current simulation graph (if any).
    let focusObj: any = null
    each(this.graph.nodes, (n: any) => {
      if (n?.focused && !focusObj) focusObj = n
    })
    const focusCenter = focusObj ? getCenter(focusObj) : boxCenter.copy()

    const nbs = this.getNeighbors(node)
    const nbCount = size(nbs)

    // Estimate a reasonable size for the *new* object (it won't have DOM-measured width/height yet).
    let estW = 200
    let estH = 120
    if (nbCount > 0) {
      let sumW = 0
      let sumH = 0
      let counted = 0
      each(nbs, (nb: any) => {
        if (nb?.width != null && nb.width > 0 && nb?.height != null && nb.height > 0) {
          sumW += nb.width
          sumH += nb.height
          counted += 1
        }
      })
      if (counted > 0) {
        estW = Math.max(80, Math.min(500, sumW / counted))
        estH = Math.max(40, Math.min(350, sumH / counted))
      }
    }

    if (node.to || node.from) {
      estW *= 0.8
      estH *= 0.7
    }

    const pad = 12
    const minX = pad
    const minY = pad
    let maxX = this.dimensions.box.width - estW - pad
    let maxY = this.dimensions.box.height - estH - pad
    if (maxX < minX) maxX = minX
    if (maxY < minY) maxY = minY

    // Anchor point (center coordinates) is based on neighbors or focus.
    let anchorCenter: any = null

    if (node.to || node.from) {
      const fromNode = this.graph.nodes[node.from]
      const toNode = this.graph.nodes[node.to]
      if (fromNode != null && toNode != null) {
        const fromC = getCenter(fromNode)
        const toC = getCenter(toNode)
        const mid = fromC.add(toC).div(2)
        const dir = toC.sub(fromC)
        if (dir.magnitude && dir.magnitude() > 0) {
          const orth = dir.normalize().rotate(Math.PI / 2.0)
          const sign = (hash32(node.id) & 1) === 0 ? 1 : -1
          const offset = (physicsSettings.springLength || 20) * 0.9
          anchorCenter = mid.add(orth.mul(offset * sign))
        }
      }
    }

    if (anchorCenter == null) {
      if (nbCount > 0) {
        const sum = new Vector(0, 0)
        each(nbs, (nb: any) => {
          const c = getCenter(nb)
          sum.add_m(c.x, c.y)
        })
        anchorCenter = sum.div(nbCount)
      } else {
        anchorCenter = focusCenter.copy()
      }
    }

    // Depth-aware ring preference around focus (soft constraint).
    const depth = node.depth || 1
    const ringStep = (physicsSettings.springLength || 20) * 7
    const maxR = Math.min(this.dimensions.box.width, this.dimensions.box.height) * 0.4
    const preferredR = Math.min(ringStep * Math.min(6, Math.max(1, depth)), maxR)

    // Deterministic golden-angle spiral around anchor.
    const seed = hash32(node.id)
    const angleOffset = ((seed % 360) * Math.PI) / 180.0
    const golden = 2.399963229728653 // golden angle
    const step = (physicsSettings.springLength || 20) * 1.8
    const startK = seed % 13
    const maxK = 160

    let best: any = null
    let bestScore = Infinity

    const boxW = this.dimensions.box.width
    const boxH = this.dimensions.box.height
    const wallMargin = Math.min(boxW, boxH) * 0.15

    // Precompute obstacle boxes (expanded), skipping the object being added (not in graph yet).
    const obstacles: any[] = []
    each(this.graph.nodes, (n: any) => {
      if (!(n?.width != null && n?.height != null)) return
      if (!(n.width > 0 && n.height > 0)) return
      const b = (n.getBox && n.getBox()) || new Box(new Vector(n.position.x, n.position.y), n.width, n.height)
      obstacles.push(new Box(new Vector(b.tl.x - pad, b.tl.y - pad), b.width + 2 * pad, b.height + 2 * pad))
    })

    const scoreCandidate = (topLeft: any) => {
      // Keep in bounds.
      const x = clamp(topLeft.x, minX, maxX)
      const y = clamp(topLeft.y, minY, maxY)
      const cand = new Box(new Vector(x, y), estW, estH)

      // Hard collision penalty.
      let collisions = 0
      for (const ob of obstacles) {
        if (isOverlapping(cand, ob)) {
          collisions += 1
          if (collisions > 0) break
        }
      }

      const center = new Vector(x + estW / 2.0, y + estH / 2.0)
      const dAnchor = center.sub(anchorCenter).magnitude()
      const dFocus = center.sub(focusCenter).magnitude()
      const ringErr = dFocus - preferredR

      // Penalize proximity to viewport walls.
      const dLeft = center.x
      const dRight = boxW - center.x
      const dTop = center.y
      const dBottom = boxH - center.y
      const nearestWall = Math.min(dLeft, dRight, dTop, dBottom)
      let wallPenalty = 0
      if (nearestWall < wallMargin) {
        const wallRatio = (wallMargin - nearestWall) / wallMargin
        wallPenalty = wallRatio * wallRatio * 5000
      }

      let score = 0
      score += dAnchor * dAnchor * 0.002
      score += ringErr * ringErr * 0.0006
      score += wallPenalty
      score += collisions * 1e9
      return { score, x, y }
    }

    // Evaluate anchor itself first (k=0).
    const firstTopLeft = new Vector(anchorCenter.x - estW / 2.0, anchorCenter.y - estH / 2.0)
    const first = scoreCandidate(firstTopLeft)
    best = first
    bestScore = first.score

    for (let k = 0; k < maxK; k += 1) {
      const kk = k + startK
      const r = step * Math.sqrt(kk + 1)
      const theta = kk * golden + angleOffset
      const cX = anchorCenter.x + Math.cos(theta) * r
      const cY = anchorCenter.y + Math.sin(theta) * r
      const topLeft = new Vector(cX - estW / 2.0, cY - estH / 2.0)
      const res = scoreCandidate(topLeft)
      if (res.score < bestScore) {
        bestScore = res.score
        best = res
        if (bestScore < 1e6 && k >= 20) break
      }
    }

    return new Vector(best.x, best.y)
  }

  getInitialPosition(node: any) {
    if (node.initialPosition?.x != null && node.initialPosition?.y != null) {
      return new Vector(node.initialPosition.x, node.initialPosition.y)
    }

    const boxW = this.dimensions.box.width
    const boxH = this.dimensions.box.height
    if (boxW <= 0 || boxH <= 0) {
      return new Vector(this.dimensions.center.x, this.dimensions.center.y)
    }

    const getCenter = (obj: any) => {
      if (obj?.getPosition) return obj.getPosition()
      return new Vector(obj.position.x, obj.position.y)
    }

    // Collect neighbor centers.
    const nbs = this.getNeighbors(node)
    const nbCenters: any[] = []
    each(nbs, (nb: any) => {
      nbCenters.push(getCenter(nb))
    })

    // Compute neighbor average position. Fall back to focus or viewport center.
    let neighborAvg: any = null
    if (nbCenters.length > 0) {
      const sum = new Vector(0, 0)
      each(nbCenters, (c: any) => sum.add_m(c.x, c.y))
      neighborAvg = sum.div(nbCenters.length)
    } else {
      let focusObj: any = null
      each(this.graph.nodes, (n: any) => {
        if (n?.focused && !focusObj) focusObj = n
      })
      neighborAvg = focusObj ? getCenter(focusObj) : new Vector(boxW / 2, boxH / 2)
    }

    // Collect existing object centers for spacing calculation.
    const objectCenters: any[] = []
    each(this.graph.nodes, (n: any) => {
      objectCenters.push(getCenter(n))
    })

    // The max meaningful spacing distance — beyond this, more distance doesn't help.
    const spacingCap = Math.min(boxW, boxH) * 0.35

    const pad = 12
    const gridCols = 20
    const gridRows = 20
    const cellW = (boxW - 2 * pad) / (gridCols - 1)
    const cellH = (boxH - 2 * pad) / (gridRows - 1)

    // Score all grid points on spacing.
    const candidates: { pt: any; spacingScore: number }[] = []

    for (let row = 0; row < gridRows; row += 1) {
      for (let col = 0; col < gridCols; col += 1) {
        const x = pad + col * cellW
        const y = pad + row * cellH

        const pt = new Vector(x, y)

        // Distance from closest existing object or wall (higher = more space = better).
        let minDist = Math.min(x, y, boxW - x, boxH - y)
        for (const oc of objectCenters) {
          const d = pt.sub(oc).magnitude()
          if (d < minDist) minDist = d
        }
        if (objectCenters.length === 0) minDist = Math.min(minDist, spacingCap)
        const spacingScore = Math.min(minDist, spacingCap) / spacingCap

        candidates.push({ pt, spacingScore })
      }
    }

    // Take the top 20% by spacing score, then pick the one closest to neighbor average.
    candidates.sort((a, b) => b.spacingScore - a.spacingScore)
    const topCount = Math.max(1, Math.ceil(candidates.length * 0.05))
    const topCandidates = candidates.slice(0, topCount)

    let bestPos: any = null
    let bestNbDist = Infinity
    for (const c of topCandidates) {
      const d = c.pt.sub(neighborAvg).magnitude()
      if (d < bestNbDist) {
        bestNbDist = d
        bestPos = c.pt
      }
    }

    const result = bestPos || new Vector(boxW / 2, boxH / 2)

    // DEBUG toggle — set to true to enable step-through debug visualization
    const DEBUG_PLACEMENT = false
    if (DEBUG_PLACEMENT) {
      if (!this._debugPending) this._debugPending = new Set()
      this._debugPending.add(node.id)
      if (!this._debugQueue) this._debugQueue = []
      this._debugQueue.push({
        id: node.id,
      placement: {
        topCandidates: topCandidates.map((c: any) => ({ x: c.pt.x, y: c.pt.y })),
        chosen: { x: result.x, y: result.y },
        neighborCenters: nbCenters.map((c: any) => ({ x: c.x, y: c.y }))
      },
      content: node.content,
      node
    })
    if (this._debugQueue.length === 1) {
      setTimeout(() => this._debugStep(), 0)
    }
    } // end DEBUG_PLACEMENT

    return result
  }

  _debugStep() {
    if (!this._debugQueue || this._debugQueue.length === 0) {
      this._debugPlacement = null
      this._debugPending = null
      this.render()
      return
    }
    const item = this._debugQueue.shift()
    this._debugPlacement = item.placement
    this._debugPending.delete(item.id)
    this.render()
    console.log('placing:', item.content, item.node)
    // debugger // red = top candidates, cyan = neighbors, green = chosen
    setTimeout(() => this._debugStep(), 0)
  }

  ensureInside(n: any) {
    const minX = 0
    const maxX = this.dimensions.box.width - n.width
    const minY = 0
    const maxY = this.dimensions.box.height - n.height
    if (n.position.x < minX) n.position.x = minX
    if (n.position.x > maxX && maxX > minX) n.position.x = maxX
    if (n.position.y < minY) n.position.y = minY
    if (n.position.y > maxY && maxY > minY) n.position.y = maxY
  }
}

class Control extends Point {
  [key: string]: any

  constructor(attrs: any) {
    super(attrs)
    const { id } = attrs
    this.id = id
  }
}

class Node extends Point {
  [key: string]: any

  constructor(attrs: any) {
    super(attrs)
    const { id, neighbors, isMore, more, content } = attrs
    this.id = id
    this.neighbors = neighbors
    this.isMore = isMore
    this.more = more
    this.content = content
    this.zoom = 100
  }

  getZoom(max: any) {
    const curZoom = this.zoom || 1
    let targetZoom = Math.floor(100 / Math.pow(this.depth, 0.6))
    const curDims = new Vector(this.width, this.height)
    const targetDims = curDims.div(curZoom).mul(targetZoom)
    if (targetDims.x > max.x) {
      const xZoom = (max.x * curZoom) / curDims.x
      if (xZoom < targetZoom) targetZoom = xZoom
    }
    if (targetDims.y > max.y) {
      const yZoom = (max.y * curZoom) / curDims.y
      if (yZoom < targetZoom) targetZoom = yZoom
    }
    return targetZoom
  }

  update(n: any) {
    const { focused, depth, color, bold, preserved, content } = n
    this.focused = focused
    this.depth = depth
    this.color = color
    this.bold = bold
    this.preserved = preserved
    this.content = content
  }
}

class Edge extends Node {
  [key: string]: any

  constructor(attrs: any) {
    super(attrs)
    const { id, from, to, directionality, direction, directionSoft, visible, spring, stiffness, springLength, thickness } = attrs
    this.id = id
    this.from = from
    this.to = to
    this.directionality = directionality
    this.direction = direction || 'free'
    this.directionSoft = Boolean(directionSoft)
    this.visible = visible !== false
    this.spring = spring !== false
    this.stiffness = stiffness
    this.springLength = springLength
    this.thickness = thickness
  }

  override getZoom(max: any) {
    const cur = this.zoom || 1
    let target = Math.floor(75 / Math.pow(this.depth, 0.6))
    const base = new Vector(this.width, this.height)
    base.div_m(cur).mul_m(100)
    if ((base.x * target) / 100 > max.x) {
      target = (max.x * 100) / base.x
    }
    if ((base.y * target) / 100 > max.y) {
      target = (max.y * 100) / base.y
    }
    return target
  }

  override update(e: any) {
    super.update(e)
    const { from, to, directionality, color, bold, direction, directionSoft, visible, spring, stiffness, springLength, thickness } = e
    this.from = from
    this.to = to
    this.directionality = directionality
    this.color = color
    this.bold = bold
    this.direction = direction || 'free'
    this.directionSoft = Boolean(directionSoft)
    this.visible = visible !== false
    this.spring = spring !== false
    this.stiffness = stiffness
    this.springLength = springLength
    this.thickness = thickness
  }

  setDirectionality(directionality: any) {
    this.directionality = directionality
  }

  override getLineWidth() {
    return this.thickness || 1
  }

  hasVisibleLabel() {
    if (isEmbeddedReadOnly) {
      return Boolean(this.content)
    }
    return Boolean(this.content) || this.depth <= 2
  }
}

export default ForceDirectedLayout
